import React, { useRef } from 'react';
import { Loader2, Plus, Upload } from 'lucide-react';
import {
  formatFileSize,
  formatRelativeTime,
  getFileVisualClasses,
  getStatusBadge,
  getUploadId
} from './docIntelUtils';

const DocIntelSidebar = ({
  uploads,
  selectedUploadId,
  onSelect,
  onUploadFiles,
  onRetryUpload,
  retryingUploadId,
  isUploading
}) => {
  const fileInputRef = useRef(null);

  const handleFiles = (files) => {
    if (!files?.length || isUploading) return;
    onUploadFiles(Array.from(files));
  };

  const needsAttention = uploads.filter(
    (u) => u.status === 'review_pending' || u.status === 'failed'
  ).length;

  return (
    <aside className="flex w-full shrink-0 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.03] lg:w-[17.5rem]">
      <div className="border-b border-slate-100 bg-gradient-to-r from-teal-50/60 to-white px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Documents</p>
            <p className="text-xs text-slate-500">
              {uploads.length} file{uploads.length === 1 ? '' : 's'} · this deal
              {needsAttention > 0 && (
                <span className="ml-1 font-medium text-amber-700">
                  · {needsAttention} need{needsAttention === 1 ? 's' : ''} action
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#2A565E]/20 bg-[#2A565E] text-white shadow-sm transition hover:bg-[#234952] disabled:opacity-50"
            title="Add files">
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" strokeWidth={2.25} />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="sr-only"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp,.txt,application/pdf,image/*,text/plain"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <ul className="max-h-[min(420px,50vh)] flex-1 overflow-y-auto">
        {uploads.map((upload) => {
          const id = getUploadId(upload);
          const badge = getStatusBadge(upload.status);
          const isActive = id === selectedUploadId;
          const visual = getFileVisualClasses(upload.original_filename, upload.mime_type);

          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => onSelect(id)}
                className={`relative w-full border-l-[3px] px-3 py-3 text-left transition ${
                  isActive
                    ? 'border-l-[#2A565E] bg-teal-50/40'
                    : 'border-l-transparent hover:bg-slate-50/80'
                }`}>
                <div className="flex gap-2.5">
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold ring-1 ${visual.className}`}>
                    {visual.label}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-1">
                      <p
                        className="truncate text-sm font-medium text-slate-900"
                        title={upload.original_filename}>
                        {upload.original_filename || 'Untitled'}
                      </p>
                      <span
                        className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${badge.className}`}>
                        {badge.label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      {formatFileSize(upload.size_bytes) || '—'}
                      {upload.created_at ? ` · ${formatRelativeTime(upload.created_at)}` : ''}
                    </p>
                    {upload.status === 'failed' && upload.error_message && (
                      <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-red-600">
                        {upload.error_message}
                        {onRetryUpload && (
                          <>
                            {' '}
                            <button
                              type="button"
                              disabled={retryingUploadId === id}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRetryUpload(id);
                              }}
                              className="font-semibold text-teal-800 underline decoration-teal-800/40 underline-offset-2 hover:text-teal-900 disabled:opacity-50">
                              {retryingUploadId === id ? 'Retrying…' : 'Retry'}
                            </button>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        className={`mx-3 mb-3 cursor-pointer rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 px-3 py-3 text-center transition hover:border-teal-300 hover:bg-teal-50/40 ${
          isUploading ? 'pointer-events-none opacity-60' : ''
        }`}>
        <Upload className="mx-auto mb-1 h-4 w-4 text-teal-700" />
        <p className="text-xs font-medium text-slate-600">Drop files or browse</p>
        <p className="mt-0.5 text-[10px] text-slate-400">PDF, JPG, PNG · up to 25 MB</p>
      </div>
    </aside>
  );
};

export default DocIntelSidebar;
